import { useCallback, useEffect, useState } from "react";
import type { OpportunityFormatKey } from "@/types/opportunity";
import {
  DEFAULT_PROPOSAL_ALTERNATE_KEY,
  DEFAULT_PROPOSAL_ALTERNATE_PAGES,
  type ProposalAlternate,
  type ProposalAlternatePage,
  type ProposalBuiltinPageKind,
} from "@/lib/opportunity/proposalAlternateTypes";
import {
  deleteProposalAlternate,
  listProposalAlternates,
  upsertProposalAlternate,
} from "@/lib/opportunity/proposalAlternateClient";
import { normalizeAlternatePages } from "@/lib/opportunity/proposalAlternateApply";
import { listProposalPages } from "@/lib/opportunity/proposalPageClient";
import type { ProposalPage } from "@/lib/opportunity/proposalPageTypes";
import {
  ensurePrimaryProposalPackage,
  isPrimaryProposalPackageKey,
  withPrimaryPageIds,
} from "@/lib/opportunity/proposalPrimaryPackage";
import { ProposalAlternatePackageEditor } from "./ProposalAlternatePackageEditor";
import { ProposalPackageList } from "./ProposalPackageList";

type Props = {
  formatKey: OpportunityFormatKey;
  productType: string;
  onChanged?: () => void;
  onPreviewPackage?: (alternateKey: string) => void;
};

function emptyDraft(sortOrder: number): Omit<
  ProposalAlternate,
  "id" | "organization_id" | "created_at" | "updated_at"
> {
  return {
    format_key: "generic",
    product_type: "*",
    alternate_key: "",
    label: "",
    description: null,
    sort_order: sortOrder,
    is_default: false,
    archetype: "proposal",
    pages: DEFAULT_PROPOSAL_ALTERNATE_PAGES.map((p) => ({ ...p })),
  };
}

function sortPackages(alts: ProposalAlternate[]): ProposalAlternate[] {
  return [...alts].sort((a, b) => {
    const aPrimary = isPrimaryProposalPackageKey(a.alternate_key) ? 0 : 1;
    const bPrimary = isPrimaryProposalPackageKey(b.alternate_key) ? 0 : 1;
    if (aPrimary !== bPrimary) return aPrimary - bPrimary;
    return a.sort_order - b.sort_order || a.label.localeCompare(b.label);
  });
}

export function ProposalAlternatesSection({
  formatKey,
  productType,
  onChanged,
  onPreviewPackage,
}: Props) {
  const [alternates, setAlternates] = useState<ProposalAlternate[]>([]);
  const [libraryPages, setLibraryPages] = useState<ProposalPage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [docPagesSupported, setDocPagesSupported] = useState(true);
  const [draftKey, setDraftKey] = useState("");
  const [draftLabel, setDraftLabel] = useState("");
  const [draftPages, setDraftPages] = useState<ProposalAlternatePage[]>(
    DEFAULT_PROPOSAL_ALTERNATE_PAGES.map((p) => ({ ...p })),
  );
  const [editingKey, setEditingKey] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    const [altRes, pageRes] = await Promise.all([
      listProposalAlternates({ format_key: formatKey, product_type: productType }),
      listProposalPages({ format_key: formatKey, product_type: productType, ensure: true }),
    ]);
    if (!altRes.ok) {
      setLoading(false);
      setError(altRes.error);
      setAlternates([]);
      return;
    }

    let alts = altRes.data.alternates.filter(
      (a) => (a.archetype ?? "proposal") === "proposal",
    );
    const library =
      pageRes.ok && pageRes.data.pages_supported !== false ? pageRes.data.pages : [];
    setLibraryPages(library);
    setDocPagesSupported(altRes.data.doc_document_id_supported !== false);

    // Auto-populate Primary when pages exist but no package has been assembled.
    if (alts.length === 0 && library.length > 0) {
      const ensured = await ensurePrimaryProposalPackage({ formatKey, productType });
      if (ensured.ok && ensured.created) {
        const again = await listProposalAlternates({
          format_key: formatKey,
          product_type: productType,
        });
        if (again.ok) {
          alts = again.data.alternates.filter(
            (a) => (a.archetype ?? "proposal") === "proposal",
          );
          onChanged?.();
        }
      } else if (!ensured.ok) {
        setError(ensured.error);
      }
    }

    setAlternates(sortPackages(alts));
    setLoading(false);
  }, [formatKey, productType, onChanged]);

  useEffect(() => {
    void reload();
  }, [reload]);

  function startEdit(alt: ProposalAlternate) {
    setEditingKey(alt.alternate_key);
    setDraftKey(alt.alternate_key);
    setDraftLabel(alt.label);
    setDraftPages(withPrimaryPageIds(normalizeAlternatePages(alt.pages), libraryPages));
  }

  function startNew() {
    setEditingKey("__new__");
    const draft = emptyDraft(alternates.length);
    setDraftKey("");
    setDraftLabel("");
    setDraftPages(withPrimaryPageIds(draft.pages, libraryPages));
  }

  function togglePage(pageKind: ProposalBuiltinPageKind) {
    setDraftPages((prev) =>
      prev.map((p) => (p.page_kind === pageKind ? { ...p, included: !p.included } : p)),
    );
  }

  function setPageLibraryId(pageKind: ProposalBuiltinPageKind, proposalPageId: string) {
    setDraftPages((prev) =>
      prev.map((p) =>
        p.page_kind === pageKind ? { ...p, proposal_page_id: proposalPageId || null } : p,
      ),
    );
  }

  function movePage(pageKind: ProposalBuiltinPageKind, direction: "up" | "down") {
    setDraftPages((prev) => {
      const sorted = normalizeAlternatePages(prev);
      const builtin = sorted.filter((p) => p.page_kind !== "doc_ref");
      const docs = sorted.filter((p) => p.page_kind === "doc_ref");
      const idx = builtin.findIndex((p) => p.page_kind === pageKind);
      const swap = direction === "up" ? idx - 1 : idx + 1;
      if (idx < 0 || swap < 0 || swap >= builtin.length) return prev;
      const nextBuiltin = [...builtin];
      [nextBuiltin[idx], nextBuiltin[swap]] = [nextBuiltin[swap]!, nextBuiltin[idx]!];
      return normalizeAlternatePages([
        ...nextBuiltin.map((p, i) => ({ ...p, sort_order: i })),
        ...docs,
      ]);
    });
  }

  async function saveDraft(opts?: { keepEditing?: boolean }): Promise<{ ok: boolean; key?: string }> {
    const key = draftKey.trim().toLowerCase();
    const label = draftLabel.trim();
    if (!/^[a-z][a-z0-9_]{0,31}$/.test(key)) {
      setError("Package key must be lowercase letters, numbers, underscores (start with letter).");
      return { ok: false };
    }
    if (editingKey === "__new__" && isPrimaryProposalPackageKey(key)) {
      setError(`Key "${DEFAULT_PROPOSAL_ALTERNATE_KEY}" is reserved for the Primary package.`);
      return { ok: false };
    }
    if (!label) {
      setError("Label is required.");
      return { ok: false };
    }
    setError(null);
    const r = await upsertProposalAlternate({
      format_key: formatKey,
      product_type: productType,
      alternate_key: key,
      label,
      sort_order: isPrimaryProposalPackageKey(key) ? 0 : alternates.length,
      is_default: isPrimaryProposalPackageKey(key) || alternates.length === 0,
      pages: normalizeAlternatePages(draftPages),
    });
    if (!r.ok) {
      setError(r.error);
      return { ok: false };
    }
    if (!opts?.keepEditing) setEditingKey(null);
    else if (editingKey === "__new__") setEditingKey(key);
    await reload();
    onChanged?.();
    return { ok: true, key };
  }

  async function previewDraft() {
    const r = await saveDraft({ keepEditing: true });
    if (r.ok && r.key) onPreviewPackage?.(r.key);
  }

  async function removeAlternate(alternateKey: string) {
    if (isPrimaryProposalPackageKey(alternateKey)) return;
    if (!window.confirm(`Delete package "${alternateKey}"?`)) return;
    const r = await deleteProposalAlternate({
      format_key: formatKey,
      product_type: productType,
      alternate_key: alternateKey,
    });
    if (!r.ok) {
      setError(r.error);
      return;
    }
    if (editingKey === alternateKey) setEditingKey(null);
    await reload();
    onChanged?.();
  }

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Packages</h3>
        <p className="mt-1 text-xs text-gray-600 dark:text-zinc-400">
          Assemble playlists of library pages (plus optional Document Builder attachments). Primary
          is created when you first edit pages — edit it like any other package. Sales picks a
          package when previewing or saving.
        </p>
      </div>

      {error ? <p className="text-xs text-red-700 dark:text-red-300">{error}</p> : null}

      {alternates.length === 0 && !editingKey ? (
        <p className="text-xs text-amber-800 dark:text-amber-200">
          {loading
            ? "Loading packages…"
            : "No packages yet — edit a page (or refresh) to assemble Primary."}
        </p>
      ) : (
        <ProposalPackageList
          alternates={alternates}
          onEdit={startEdit}
          onDelete={(key) => void removeAlternate(key)}
        />
      )}

      {editingKey ? (
        <ProposalAlternatePackageEditor
          draftKey={draftKey}
          draftLabel={draftLabel}
          draftPages={draftPages}
          editingKey={editingKey}
          libraryPages={libraryPages}
          docPagesSupported={docPagesSupported}
          onDraftKeyChange={setDraftKey}
          onDraftLabelChange={setDraftLabel}
          onDraftPagesChange={setDraftPages}
          onTogglePage={togglePage}
          onSetPageLibraryId={setPageLibraryId}
          onMovePage={movePage}
          onSave={() => void saveDraft()}
          onPreview={onPreviewPackage ? () => void previewDraft() : undefined}
          onCancel={() => setEditingKey(null)}
        />
      ) : (
        <button
          type="button"
          disabled={loading}
          onClick={startNew}
          className="rounded border border-gray-300 px-3 py-1.5 text-xs dark:border-zinc-600"
        >
          + Add package
        </button>
      )}
    </div>
  );
}
